import React from "react";
import { FileText, ShieldCheck, Scale, AlertCircle } from "lucide-react";
import { Helmet } from "react-helmet-async";

export function TermsOfService() {
  return (
    <div className="max-w-4xl mx-auto py-10 px-4 sm:px-6 lg:px-8">
      <Helmet>
        <title>Terms of Service – 247 DevTools Hub</title>
        <meta name="description" content="Terms of Service for 247 DevTools Hub. Guidelines, conditions, and usage rules for our free browser-based developer utilities." />
      </Helmet>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-10 space-y-8">
        
        {/* Header */}
        <div className="text-center border-b border-slate-100 pb-8">
          <div className="inline-flex p-3 bg-blue-50 text-blue-600 rounded-full mb-4">
            <Scale className="w-8 h-8" />
          </div>
          <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Terms of Service</h1>
          <p className="text-sm text-slate-500 mt-2">Effective Date: July 23, 2026</p>
        </div>

        {/* Highlights */}
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 flex flex-col items-center text-center">
            <ShieldCheck className="w-6 h-6 text-blue-600 mb-2" />
            <h3 className="font-semibold text-slate-900 text-sm">Free to Use</h3>
            <p className="text-xs text-slate-500 mt-1">Our web utilities are 100% free for commercial & personal projects.</p>
          </div>
          <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 flex flex-col items-center text-center">
            <FileText className="w-6 h-6 text-blue-600 mb-2" />
            <h3 className="font-semibold text-slate-900 text-sm">No Account Needed</h3>
            <p className="text-xs text-slate-500 mt-1">Instant access without mandatory registrations or subscriptions.</p>
          </div>
          <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 flex flex-col items-center text-center">
            <AlertCircle className="w-6 h-6 text-blue-600 mb-2" />
            <h3 className="font-semibold text-slate-900 text-sm">As-Is Warranty</h3>
            <p className="text-xs text-slate-500 mt-1">Provided as-is to assist developer workflows safely.</p>
          </div>
        </div>

        {/* Detailed Sections */}
        <div className="prose prose-slate max-w-none text-slate-600 space-y-6 text-sm leading-relaxed">
          
          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-2">1. Acceptance of Terms</h2>
            <p>
              By accessing and using <strong>247 DevTools Hub</strong> (accessible at our website domain), you agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree with any of these terms, you are prohibited from using or accessing this site.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-2">2. Description of Utilities</h2>
            <p>
              247 DevTools Hub provides a comprehensive suite of online utilities for software engineers, web developers, designers, and system administrators. These include JSON formatters, cURL converters, text tools, cryptographic hashers, regex testers, SVG optimizers, network calculators, and AdSense audit helpers.
            </p>
            <p className="mt-2">
              All data processing operations occur client-side within your browser using modern Web APIs and local JavaScript engines.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-2">3. User Conduct and Restrictions</h2>
            <p>You agree not to use our utilities for any illegal or unauthorized purpose, including:</p>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>Attempting to overload, compromise, or disrupt the website's infrastructure.</li>
              <li>Automated scraping or bot spamming that degrades performance for human users.</li>
              <li>Utilizing generated security tokens or hashes for unauthorized hacking activities.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-2">4. Intellectual Property</h2>
            <p>
              The code generated, formatted, or parsed by you using our tools remains 100% your own property. 247 DevTools Hub claims no ownership or license rights over any text, JSON payload, or code snippet processed on our site.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-2">5. Third-Party Advertising (Google AdSense)</h2>
            <p>
              This website displays ads served by Google AdSense and third-party advertising networks. By using our site, you acknowledge that third-party vendors may use cookies (such as the DART cookie) to serve ads based on your visit history. For more information on cookie practices, please read our <a href="/privacy" className="text-blue-600 hover:underline font-medium">Privacy Policy</a>.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-2">6. Disclaimer of Warranty & Limitation of Liability</h2>
            <p>
              The materials and tools on 247 DevTools Hub are provided on an 'as is' basis. We make no warranties, expressed or implied, and hereby disclaim and negate all other warranties including, without limitation, implied warranties or conditions of merchantability or fitness for a particular purpose.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-2">7. Contact & Inquiries</h2>
            <p>
              If you have any questions regarding these Terms of Service, please reach out through our <a href="/contact" className="text-blue-600 hover:underline font-medium">Contact Form</a>.
            </p>
          </section>

        </div>

      </div>
    </div>
  );
}
